import React, { useState, useEffect } from 'react';
import supabase from '../supabase/supabase';
import { FiX, FiUploadCloud } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export default function UploadFlyerModal({ isOpen, onClose }) {
  const [categories, setCategories] = useState([]);
  const [email, setEmail] = useState('');
  const [file, setFile] = useState(null);
  const [categoryId, setCategoryId] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [uploadType, setUploadType] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      setSuccess(false);
      setMsg('');
      setFile(null);
      setEmail('');
      setCategoryId('');
      setCustomCategory('');
      setUploadType('');
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    const { data } = await supabase.from('categories_1715000000').select('*').order('name');
    if (data) setCategories(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return setMsg('Please select a flyer image.');
    setLoading(true);
    setMsg('');

    try {
      // 1. Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `public_${Date.now()}_${Math.random()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('portfolio_images')
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('portfolio_images')
        .getPublicUrl(fileName);

      // 2. Insert into pending_uploads
      const { error: dbError } = await supabase.from('pending_uploads_1715000000').insert([{
        email,
        image_url: publicUrl,
        category_id: categoryId === 'other' ? null : categoryId,
        custom_category: categoryId === 'other' ? customCategory : null,
        upload_type: uploadType
      }]);

      if (dbError) throw dbError;
      
      setSuccess(true);
    } catch (error) {
      setMsg(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-retro-brown/80 p-4 backdrop-blur-sm">
      <div className="bg-retro-cream p-8 md:p-10 rounded-3xl border-4 border-retro-brown shadow-retro w-full max-w-xl relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-6 right-6 text-retro-brown hover:text-red-500 transition-colors bg-white border-2 border-retro-brown rounded-full p-2 shadow-retro-sm active:translate-y-0.5 active:shadow-retro-active">
          <SafeIcon icon={FiX} className="text-2xl font-bold" />
        </button>

        {success ? (
          <div className="text-center py-10">
            <div className="w-20 h-20 bg-retro-lime border-4 border-retro-brown rounded-full mx-auto flex items-center justify-center shadow-retro mb-6">
              <SafeIcon icon={FiUploadCloud} className="text-4xl text-retro-brown" />
            </div>
            <h2 className="font-display text-4xl text-retro-brown mb-4 uppercase tracking-wider">SUCCESS!</h2>
            <p className="text-xl font-bold text-retro-brown/80 mb-8">Your flyer has been submitted for review. Thank you!</p>
            <button onClick={onClose} className="bg-retro-teal text-white py-3 px-8 rounded-xl font-display text-xl tracking-wider border-4 border-retro-brown shadow-retro hover:shadow-retro-hover hover:-translate-y-1 active:translate-y-1 active:shadow-retro-active transition-all">
              CLOSE
            </button>
          </div>
        ) : (
          <>
            <h2 className="font-display text-4xl text-retro-brown mb-2 uppercase tracking-wider drop-shadow-[2px_2px_0_#13A09E]">UPLOAD FLYER</h2>
            <p className="font-bold text-retro-brown/70 mb-8 text-lg">Share your awesome design with the community.</p>
            
            <form onSubmit={handleSubmit} className="flex flex-col gap-6 text-left">
              <div>
                <label className="block font-bold text-retro-brown mb-2 text-lg">Email Address *</label>
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com" 
                  className="w-full p-4 bg-white border-4 border-retro-brown rounded-xl focus:outline-none focus:ring-4 focus:ring-retro-blue font-bold text-retro-brown shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
                  required 
                />
              </div>

              <div>
                <label className="block font-bold text-retro-brown mb-2 text-lg">Flyer Image *</label>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="w-full p-4 bg-white border-4 border-dashed border-retro-brown rounded-xl focus:outline-none focus:ring-4 focus:ring-retro-blue font-bold text-retro-brown shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-2 file:border-retro-brown file:text-sm file:font-bold file:bg-retro-yellow file:text-retro-brown hover:file:bg-retro-lime cursor-pointer"
                  required 
                />
              </div>

              <div>
                <label className="block font-bold text-retro-brown mb-2 text-lg">Category *</label>
                <select 
                  value={categoryId} 
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="w-full p-4 border-4 border-retro-brown rounded-xl focus:outline-none focus:ring-4 focus:ring-retro-blue font-bold text-retro-brown bg-white shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] appearance-none cursor-pointer"
                  required
                >
                  <option value="" disabled>Select a category...</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                  <option value="other">Other (Please specify)</option>
                </select>
              </div>

              {categoryId === 'other' && (
                <div className="animate-in fade-in slide-in-from-top-2">
                  <label className="block font-bold text-retro-brown mb-2 text-lg">Custom Category *</label>
                  <input 
                    type="text" 
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    placeholder="E.g. Vintage Cars" 
                    className="w-full p-4 bg-white border-4 border-retro-brown rounded-xl focus:outline-none focus:ring-4 focus:ring-retro-blue font-bold text-retro-brown shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]"
                    required 
                  />
                </div>
              )}

              <div>
                <label className="block font-bold text-retro-brown mb-3 text-lg">Are you replacing a current flyer or adding a new one? *</label>
                <div className="flex flex-col gap-3">
                  <label className="flex items-center gap-3 cursor-pointer p-4 bg-white border-4 border-retro-brown rounded-xl hover:bg-retro-cream transition-colors">
                    <input 
                      type="radio" 
                      name="uploadType" 
                      value="new" 
                      onChange={(e) => setUploadType(e.target.value)}
                      required 
                      className="w-6 h-6 accent-retro-teal cursor-pointer"
                    />
                    <span className="font-bold text-retro-brown text-lg">Adding new flyer</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-4 bg-white border-4 border-retro-brown rounded-xl hover:bg-retro-cream transition-colors">
                    <input 
                      type="radio" 
                      name="uploadType" 
                      value="replace" 
                      onChange={(e) => setUploadType(e.target.value)}
                      required 
                      className="w-6 h-6 accent-retro-teal cursor-pointer"
                    />
                    <span className="font-bold text-retro-brown text-lg">Replacing current flyer</span>
                  </label>
                </div>
              </div>
              
              <button 
                type="submit" 
                disabled={loading} 
                className="w-full bg-retro-blue text-white py-4 rounded-xl font-display text-2xl tracking-wider border-4 border-retro-brown shadow-retro hover:shadow-retro-hover hover:-translate-y-1 active:translate-y-1 active:shadow-retro-active transition-all mt-4 disabled:opacity-50"
              >
                {loading ? 'UPLOADING...' : 'SUBMIT FLYER'}
              </button>

              {msg && (
                <div className={`text-base font-bold text-center p-4 border-4 border-retro-brown rounded-xl shadow-retro-sm ${msg.toLowerCase().includes('error') ? 'bg-red-100 text-red-700' : 'bg-retro-lime text-retro-brown'}`}>
                  {msg}
                </div>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}